/**
 * 2FA Challenge Token Service
 * Handles stateless challenge token storage and retrieval
 * 
 * Challenge tokens are encrypted JWE tokens containing:
 * - OTP code (encrypted)
 * - Attempt counter
 * - User payload (id, loginId, mailId)
 * - Expiry timestamp (5 minutes)
 * 
 * Token is stored in sessionStorage (single-page session, lost on tab close)
 * and passed back to server in API calls.
 */

const CHALLENGE_TOKEN_KEY = '2fa_challenge_token';
const REMAINING_ATTEMPTS_KEY = '2fa_remaining_attempts';
const DEFAULT_OTP_ATTEMPTS = 3;

/**
 * Store challenge token in sessionStorage for OTP verification
 * @param {string} token - The encrypted challenge token from login API
 * @param {number} remainingAttempts - Number of attempts remaining (default 3)
 */
export const storeChallengeToken = (token, remainingAttempts = DEFAULT_OTP_ATTEMPTS) => {
  try {
    if (!token) {
      return false;
    }

    /* Store challenge token in sessionStorage (single-page session) */
    sessionStorage.setItem(CHALLENGE_TOKEN_KEY, token);
    sessionStorage.setItem(REMAINING_ATTEMPTS_KEY, String(remainingAttempts));

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Retrieve challenge token from sessionStorage
 * @returns {string|null} The challenge token or null if not found/expired
 */
export const retrieveChallengeToken = () => {
  try {
    const token = sessionStorage.getItem(CHALLENGE_TOKEN_KEY);

    if (!token) {
      return null;
    }

    return token;
  } catch (error) {
    return null;
  }
};

/**
 * Retrieve remaining attempts from sessionStorage
 * @returns {number} The remaining attempts or 3 (default)
 */
export const retrieveRemainingAttempts = () => {
  try {
    const attempts = sessionStorage.getItem(REMAINING_ATTEMPTS_KEY);
    return attempts ? parseInt(attempts, 10) : DEFAULT_OTP_ATTEMPTS;
  } catch (error) {
    return DEFAULT_OTP_ATTEMPTS;
  }
};

/**
 * Update challenge token with new token and remaining attempts
 * Called when server returns new token on failed verification
 * @param {string} newToken - The new challenge token from error response
 * @param {number} newAttempts - Updated remaining attempts count
 * @returns {boolean} Success indicator
 */
export const updateChallengeToken = (newToken, newAttempts) => {
  try {
    if (!newToken) {
      return false;
    }

    sessionStorage.setItem(CHALLENGE_TOKEN_KEY, newToken);
    sessionStorage.setItem(REMAINING_ATTEMPTS_KEY, String(newAttempts));

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Clear challenge token from sessionStorage
 * Called on successful verification or when navigating away from OTP page
 * @returns {boolean} Success indicator
 */
export const clearChallengeToken = () => {
  try {
    sessionStorage.removeItem(CHALLENGE_TOKEN_KEY);
    sessionStorage.removeItem(REMAINING_ATTEMPTS_KEY);

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Check if challenge token exists and is valid
 * @returns {boolean} True if token exists and is valid
 */
export const hasChallengeToken = () => {
  try {
    const token = sessionStorage.getItem(CHALLENGE_TOKEN_KEY);
    return token !== null && token !== '';
  } catch (error) {
    return false;
  }
};

/**
 * Handle 2FA response from login API
 * Stores challenge token and returns formatted response for PreLogin component
 * @param {object} responseData - Response from loginExtUser API
 * @returns {object} Formatted response with stored token
 */
export const handleTwoFaResponse = (responseData) => {
  try {
    const { challengeToken, remainingAttempts, message } = responseData;

    if (!challengeToken) {
      return {
        status: 'TWOFA_REQUIRED',
        success: false,
        error: 'No challenge token received from server'
      };
    }

    /* Store token in sessionStorage */
    const stored = storeChallengeToken(challengeToken, remainingAttempts);

    if (!stored) {
      return {
        status: 'TWOFA_REQUIRED',
        success: false,
        error: 'Failed to store challenge token'
      };
    }

    return {
      status: 'TWOFA_REQUIRED',
      success: true,
      requiresOTP: true,
      challengeToken,
      remainingAttempts: remainingAttempts || DEFAULT_OTP_ATTEMPTS,
      message: message || 'OTP required. Check your email.'
    };
  } catch (error) {
    return {
      status: 'TWOFA_REQUIRED',
      success: false,
      error: 'Failed to process 2FA response'
    };
  }
};

export default {
  storeChallengeToken,
  retrieveChallengeToken,
  retrieveRemainingAttempts,
  updateChallengeToken,
  clearChallengeToken,
  hasChallengeToken,
  handleTwoFaResponse
};
